// context/tab-context.js
import React, { createContext, useState, useCallback, useMemo } from 'react';

/**
 * Single context for both reading and updating tab data
 */
export const TabContext = createContext(null);

/**
 * TabContextProvider - Manages the currently active tab's context
 * 
 * This provider allows any tab/page component to register its default values
 * and makes those values available to global components like ChatAssistant
 */
const TabContextProvider = ({ children }) => {
  // Main state holding the current tab's context
  const [tabContext, setTabContext] = useState({
    tabName: null,              // Name of the active tab (e.g., 'brand-monitoring')
    execution_date: null,       // Selected execution date
    brand: null,                // Selected brand name
    brandContext: null,         // Brand-specific context data
    category: null,             // Selected category (if applicable)
    persona: null,              // Selected persona (if applicable)
    llmModel: null,            // Selected LLM model (if applicable)
    customData: {},            // Any additional tab-specific data
    timestamp: null,           // When this context was last updated
  });

  /**
   * Update the tab context with new values
   * This should be called when a tab component mounts or its values change
   */
  const updateTabContext = useCallback((newContext) => {
    setTabContext((prev) => {
      // Only update if values actually changed (prevents unnecessary re-renders)
      const hasChanges = Object.keys(newContext).some(
        key => prev[key] !== newContext[key]
      );

      if (!hasChanges) return prev;

      return {
        ...prev,
        ...newContext,
        timestamp: Date.now(),
      };
    });
  }, []);

  /**
   * Clear the tab context
   * Useful when navigating away or resetting state
   */
  const clearTabContext = useCallback(() => {
    setTabContext({
      tabName: null,
      execution_date: null,
      brand: null,
      brandContext: null,
      category: null,
      persona: null,
      llmModel: null,
      customData: {},
      timestamp: null,
    });
  }, []);

  /**
   * Partially update specific fields
   * Useful for updating just one value without providing all fields
   */
  const updateTabField = useCallback((field, value) => {
    setTabContext((prev) => {
      if (prev[field] === value) return prev;
      
      return {
        ...prev,
        [field]: value,
        timestamp: Date.now(),
      };
    });
  }, []);

  /**
   * Get a specific field value
   * Returns the value or a default
   */
  const getTabField = useCallback((field, defaultValue = null) => {
    return tabContext[field] ?? defaultValue;
  }, [tabContext]);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      // Current tab data
      tabName: tabContext.tabName,
      execution_date: tabContext.execution_date,
      brand: tabContext.brand,
      brandContext: tabContext.brandContext,
      category: tabContext.category,
      persona: tabContext.persona,
      llmModel: tabContext.llmModel,
      customData: tabContext.customData,
      timestamp: tabContext.timestamp,
      
      // Methods to update context
      updateTabContext,
      clearTabContext,
      updateTabField,
      getTabField,
    }),
    [
      tabContext,
      updateTabContext,
      clearTabContext,
      updateTabField,
      getTabField,
    ]
  );

  return (
    <TabContext.Provider value={contextValue}>
      {children}
    </TabContext.Provider>
  );
};

export default TabContextProvider;